import AdminPageLoader from "components/AdminPageLoader";
import { portalPageMeta } from "layouts/Admin2/pageMeta";
import PageHeading from "admin/components/PageHeading";
import usePaymentMethodAvailability from "api/PaymentsResource/usePaymentMethodAvailability";
import useUpdatePaymentMethodAvailability from "api/PaymentsResource/useUpdatePaymentMethodAvailability";
import useCurrencies from "api/CurrencyResource/useCurrencies";
import { Link } from "react-router-dom";
import Admin2 from "layouts/Admin2";
import { useEffect, useMemo, useRef, useState } from "react";
import { FaInfoCircle } from "react-icons/fa";
import UnavailableMessageNotice from "components/UnavailableMessageNotice";

const buildFormState = (availabilityMap) =>
	Object.entries(availabilityMap || {}).reduce((state, [apiKey, config]) => {
		state[apiKey] = {
			enabled: config.enabled !== false,
			message: config.message || "",
            supported_currencies: config.supported_currencies || [],
		};
		return state;
	}, {});

const PaymentMethods = () => {
    const { data: currencies = [], isLoading: currenciesLoading, isError: currenciesError, refetch: refetchCurrencies } = useCurrencies();
	const { data: availabilityMap, isLoading, isError, refetch } = usePaymentMethodAvailability();
	const { mutate: updateAvailability, isLoading: isSaving } = useUpdatePaymentMethodAvailability();
	const [formState, setFormState] = useState({});
	const [isDirty, setIsDirty] = useState(false);
    const [currencySearch, setCurrencySearch] = useState("");
	const hasInitialized = useRef(false);
    const hasEmptyMethod = Object.values(formState).some((method) => method.enabled && !method.supported_currencies?.length);

	const methods = useMemo(() => {
		if (!availabilityMap) {
			return [];
		}

		return Object.entries(availabilityMap).map(([apiKey, config]) => ({
			apiKey,
			label: config.label || apiKey,
		}));
	}, [availabilityMap]);

	useEffect(() => {
		if (!availabilityMap || hasInitialized.current) {
			return;
		}

		setFormState(buildFormState(availabilityMap));
		hasInitialized.current = true;
	}, [availabilityMap]);

	const handleToggle = (apiKey) => {
		setIsDirty(true);
		setFormState((current) => ({
			...current,
			[apiKey]: {
				...current[apiKey],
				enabled: !current[apiKey]?.enabled,
			},
		}));
	};

	const handleMessageChange = (apiKey, message) => {
		setIsDirty(true);
		setFormState((current) => ({
			...current,
			[apiKey]: {
				...current[apiKey],
				message,
			},
		}));
	};

	const handleSubmit = (event) => {
		event.preventDefault();

		if (!isDirty || isSaving || currenciesLoading || currenciesError || hasEmptyMethod) {
			return;
		}

		updateAvailability(
			{
				methods: Object.entries(formState).reduce((payload, [apiKey, config]) => {
					payload[apiKey] = {
						enabled: Boolean(config.enabled),
						message: config.message?.trim() || null,
                        supported_currencies: config.supported_currencies,
					};
					return payload;
				}, {}),
			},
			{
				onSuccess: (response) => {
					const savedMethods = response?.data?.methods;

					if (savedMethods) {
						setFormState(buildFormState(savedMethods));
					}

					setIsDirty(false);
				},
			},
		);
	};

    if (isError) return <Admin2 fullWidth><p role="alert" className="p-4 text-red-700">Unable to load payment settings.</p><button type="button" onClick={() => refetch()} className="p-4 underline">Retry</button></Admin2>;
	if (isLoading) return <AdminPageLoader />;

	return (
		<Admin2 fullWidth>
			<div className="portal-page-container">
				<div className="flex flex-col gap-4 w-full mb-6">
					<PageHeading title="Payment methods" description={portalPageMeta("/admin/payment-methods").description} />
					<p className="text-gray-500 text-base max-w-2xl font-light">
						Select all currencies each payment method supports and enable or disable the method.
                        Changes are saved together when you click Save changes.
					</p>
				</div>

				<Link to="/admin/currencies" className="text-akgreen underline">Manage currencies and exchange rates</Link>
                {currenciesLoading && <p role="status" className="text-sm text-gray-600">Loading currencies...</p>}
                {currenciesError && <div role="alert" className="text-sm text-red-700">Unable to load currencies. <button type="button" onClick={() => refetchCurrencies()} className="underline">Retry currencies</button></div>}
                {!currenciesLoading && !currenciesError && !currencies.length && <p role="status" className="text-sm text-gray-600">Enable currencies with exchange rates in Manage currencies to configure payment methods.</p>}
                <label className="block text-sm text-gray-700 space-y-1">
                    <span className="block">Find a currency</span>
                    <input type="search" value={currencySearch} onChange={(event) => setCurrencySearch(event.target.value)} placeholder="Search by name or code" className="w-full rounded border border-gray-300 p-2 focus:ring-akgreen" />
                </label>
                <form onSubmit={handleSubmit} className="space-y-4">
					{methods.map((method) => {
						const state = formState[method.apiKey] || {
							enabled: true,
							message: "",
                            supported_currencies: [],
						};

						return (
							<div
								key={method.apiKey}
								className="bg-white rounded-lg shadow border border-gray-200 p-5 space-y-4"
							>
								<div className="flex items-start justify-between gap-4">
									<div>
										<h2 className="text-lg font-semibold text-green-900">{method.label}</h2>
										<p className="text-xs text-gray-500 mt-1">
											Select one or more enabled currencies supported by your merchant account.
										</p>
									</div>
									<label className="inline-flex items-center gap-2 cursor-pointer">
										<span className="text-sm text-gray-600">
											{state.enabled ? "Enabled" : "Disabled"}
										</span>
										<input
											type="checkbox"
											checked={Boolean(state.enabled)}
                                            aria-label={`Enable ${method.label}`}
											onChange={() => handleToggle(method.apiKey)}
											className="portal-method-switch"
										/>
									</label>
								</div>

                                <fieldset className="space-y-2" aria-label={`${method.label} supported currencies`} disabled={currenciesLoading || currenciesError || isSaving}>
                                    <legend className="text-sm font-medium text-gray-800">Supported currencies</legend>
                                    <p className="text-xs text-gray-600">{state.supported_currencies?.length || 0} selected. A currency can be selected for several payment methods.</p>
                                    <div className="flex flex-wrap gap-4">
                                        {currencies.filter((c) => state.supported_currencies?.includes(c.code) || `${c.label} ${c.code}`.toLowerCase().includes(currencySearch.trim().toLowerCase())).map((c) => <label key={c.code} className="flex items-center gap-2 text-sm">
                                            <input type="checkbox" checked={state.supported_currencies?.includes(c.code) || false}
                                                onChange={(event) => {
                                                    const checked = event.target.checked;
                                                    setIsDirty(true);
                                                    setFormState((current) => ({ ...current, [method.apiKey]: { ...current[method.apiKey], supported_currencies: checked ? [...(current[method.apiKey].supported_currencies || []), c.code] : current[method.apiKey].supported_currencies.filter((code) => code !== c.code) } }));
                                                }} /> {c.label} ({c.code})
                                        </label>)}
                                    </div>
                                    {state.enabled && !state.supported_currencies?.length && <p role="alert" className="text-sm text-red-700">Select at least one currency.</p>}
                                </fieldset>
								{!state.enabled && (
									<div className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-3">
										<div className="flex items-start gap-2">
											<span className="flex-shrink-0 w-6 h-6 rounded-full bg-amber-200 text-amber-800 flex items-center justify-center mt-0.5">
												<FaInfoCircle size={11} />
											</span>
											<div className="min-w-0 flex-1 space-y-0.5">
												<label
													htmlFor={`unavailable-message-${method.apiKey}`}
													className="block text-xs font-semibold text-amber-900"
												>
													User-facing unavailability message
												</label>
												<p className="text-xs text-amber-800 leading-snug">
													Optional. Shown on checkout while{" "}
													<span className="font-medium">{method.label}</span> is disabled.
												</p>
											</div>
										</div>

										<div className="space-y-1.5">
											<textarea
												id={`unavailable-message-${method.apiKey}`}
												value={state.message || ""}
												onChange={(event) =>
													handleMessageChange(method.apiKey, event.target.value)
												}
												rows={3}
												maxLength={500}
												placeholder="e.g. EcoCash USD is temporarily unavailable due to scheduled maintenance."
												className="w-full rounded-md border border-amber-200 bg-white px-3 py-2 text-xs text-gray-800 placeholder-gray-400 focus:border-akgreen focus:outline-none focus:ring-1 focus:ring-akgreen resize-y"
											/>
											<div className="flex items-center justify-between gap-3 text-xs">
												<span className="text-gray-500">
													Published when you click Save changes
												</span>
												<span
													className={
														(state.message?.length || 0) > 450
															? "text-amber-700 font-medium"
															: "text-gray-400"
													}
												>
													{state.message?.length || 0}/500
												</span>
											</div>
										</div>

										<div className="space-y-1.5 pt-2 border-t border-amber-200">
											<p className="text-xs font-medium uppercase tracking-wide text-amber-700">
												Checkout preview
											</p>
											<div className="rounded-md bg-amber-100 p-2">
												<UnavailableMessageNotice
													label={method.label}
													message={state.message}
													compact
												/>
											</div>
										</div>
									</div>
								)}
							</div>
						);
					})}

					<div className="flex items-center justify-end gap-3 pt-2">
						{isDirty && (
							<span className="text-sm text-amber-700">You have unsaved changes</span>
						)}
						<button
							type="submit"
							disabled={isSaving || currenciesLoading || currenciesError || !methods.length || !isDirty || hasEmptyMethod}
							className="px-6 py-2.5 rounded-lg bg-akgreen text-white font-medium hover:bg-akgreener transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
						>
							{isSaving ? "Saving..." : "Save changes"}
						</button>
					</div>
				</form>
			</div>
		</Admin2>
	);
};

export default PaymentMethods;
